import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Footer } from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

const COUNTRIES = [
  "Afghanistan", "Algeria", "Angola", "Armenia", "Azerbaijan", "Bahrain",
  "Bangladesh", "Belarus", "Belize", "Benin", "Bhutan", "Bolivia",
  "Botswana", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
  "Cameroon", "Central African Republic", "Chad", "China", "Comoros",
  "Cote D'ivoire", "Cuba", "Dem. Rep. Of Congo", "Djibouti",
  "Dominican Republic", "Ecuador", "Egypt", "Equatorial Guinea", "Eritrea",
  "Eswatini", "Ethiopia", "Fiji", "Gabon", "Gambia", "Ghana",
  "Guinea-Bissau", "Guyana", "Haiti", "India", "Indonesia", "Iran", "Iraq",
  "Jamaica", "Jordan", "Kazakhstan", "Kenya", "Kosovo", "Kuwait",
  "Kyrgyzstan", "Laos", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Madagascar", "Malawi", "Maldives", "Mali", "Mauritania", "Mongolia",
  "Morocco", "Mozambique", "Myanmar", "Namibia", "Nepal", "Niger",
  "Nigeria", "North Korea", "Northern Mariana Islands", "Oman", "Pakistan",
  "Palestine", "Papua New Guinea", "Philippines", "Qatar",
  "Republic of the Congo", "Russia", "Rwanda", "Saudi Arabia", "Senegal",
  "Sierra Leone", "Somalia", "South Africa", "South Sudan", "Sri Lanka",
  "Suriname", "Syria", "São Tomé and Príncipe", "Tajikistan", "Tanzania",
  "Thailand", "Timor-Leste", "Togo", "Tonga", "Tunisia", "Turkmenistan",
  "Türkiye", "Uganda", "Uzbekistan", "Vanuatu", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe",
];

export const SchengenCountries = () => (
  <div className="bg-[#FAFAFA] text-slate-900 antialiased min-h-screen" data-testid="schengen-countries-page">
    <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <a
          href="/"
          data-testid="countries-logo"
          className="font-display text-2xl font-semibold tracking-tight text-slate-900"
        >
          TrioDocz<span className="text-slate-400">.</span>
        </a>
        <a
          href="/#products"
          data-testid="countries-back-link"
          className="group flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-slate-600 hover:text-slate-900 transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
          Back to products
        </a>
      </div>
    </header>

    <main className="px-6 md:px-12 pt-40 pb-28">
      <div className="max-w-[1400px] mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
        >
          Visa guide · Schengen
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl"
        >
          Who needs a Schengen visa to travel to the{" "}
          <em className="italic text-slate-500">EU?</em>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          className="mt-10 max-w-2xl space-y-5"
        >
          <p className="text-base text-slate-600 leading-relaxed">
            While there are categories and nationals of countries that are
            privileged with entering the Schengen visa-free zone, there are
            also other categories and nationals which have to go through all
            the processes of meeting requirements and attending interviews in
            order to obtain a visa that grants them permission to enter the
            Schengen Area.
          </p>
          <p className="text-base text-slate-600 leading-relaxed">
            The {COUNTRIES.length} countries whose citizens are required to
            obtain a Schengen visa in order to enter any member country of the
            Schengen Area are:
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-10"
          data-testid="countries-grid"
        >
          {COUNTRIES.map((c, i) => (
            <div
              key={c}
              data-testid="country-item"
              className="flex items-baseline justify-between gap-3 py-3.5 border-b border-slate-200"
            >
              <span className="text-sm font-medium text-slate-800">{c}</span>
              <span className="font-display text-xs text-slate-300">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-16 flex flex-wrap items-center justify-between gap-8 bg-white border border-slate-200 p-8 md:p-10"
        >
          <div>
            <p className="font-display text-2xl tracking-tight text-slate-900">
              On the list? We'll handle the paperwork.
            </p>
            <p className="text-sm text-slate-500 mt-2 max-w-md leading-relaxed">
              Requirements change — we confirm the current list and documents
              with the consulate for every file we prepare.
            </p>
          </div>
          <a
            href="/#contact"
            data-testid="countries-cta-button"
            className="group flex items-center gap-3 bg-slate-900 text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-slate-700 transition-colors duration-300"
          >
            Start your Schengen file
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </main>

    <Footer onNavigate={(t) => (window.location.href = `/${t}`)} />
  </div>
);
